import AddIcon from '@mui/icons-material/Add';
// MUI アイコン（CloseIcon）をインポート
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Modal,
  Stack,
  SxProps,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GetContextMenuItems, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useSnackbar } from 'notistack5';
// フックをインポート
import { useEffect, useRef, useState } from 'react';
// react-hook-form から useForm フックをインポート
import { useForm } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// react-redux から useDispatchとuseSelector フックをインポート
import { useDispatch, useSelector } from 'react-redux';
import { SelectUserAgGrid } from 'src/components/app-components/bonsai/SelectUserAgGrid';
import { StyledButton } from 'src/components/app-components/StyledButton';
import RHFDeskTopDatePicker from 'src/components/react-hook-form/RHFDeskTopDatePicker';
import { REMINDER_STATUS } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
import useSuggestUser from 'src/hooks/useSuggestUser';
// Redux ストアセレクターをインポート
import { RootState } from 'src/redux/store';
import palette from 'src/theme/palette';
import { ReminderEntity } from '../hooks/useReminderListGrid';
import { useValidSaveButton } from '../hooks/useValidSaveButton';
import { setReminderListData, setRowData, setSelectedUser } from '../store/enqueteCalendarSlice';
// プロパティインターフェースを定義
export interface Props {
  // リマインドID
  reminderId: string;
  // ダイアログの開閉状態保持 (true：開く、false：閉じる)
  open: boolean;
  // ダイアログクローズ時動作。openをfalseに変更
  onClose: () => void;
}
// リマインド依頼ダイアログ
export const SelectReminderUserModal: React.FC<Props> = (props) => {
  // パラメータ
  const { open, onClose } = props;
  // Redux dispatch 関数を取得
  const dispatch = useDispatch();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  // 翻訳関数を取得
  const { t } = useTranslation();
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';
  const { userSuggest, updateUserSuggest } = useSuggestUser();
  const { reminderListData, rowData, selectedUser } = useSelector((state: RootState) => state.enqueteCalendar);
  // 確認ダイアログオープン状態のステートを定義
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  // react-hook-form のメソッドを初期化、変更モードで検証を設定
  const methods = useForm({ mode: 'onChange' });
  // Validator
  const { reminderDateRule } = useValidSaveButton(methods);

  //-------------------------------------------------------------
  // useRef
  //-------------------------------------------------------------
  const gridApi = useRef<GridApi | null>(null);

  //-------------------------------------------------------------
  // グリッドの表示準備完了時の処理
  //-------------------------------------------------------------
  const onGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;
  };

  //-------------------------------------------------------------
  // 追加対象一覧に追加
  //-------------------------------------------------------------
  const onAddList = () => {
    // check input string
    const user = userSuggest.find((user) => user.key === selectedUser);
    if (!user) return enqueueSnackbar(t('answerConfirmStatus.selectConfirmerModal.message.notSelectUser'), { variant: 'error' });

    // check duplicated
    const addRowData = JSON.stringify(user.entity);
    const exists = rowData.filter((rowdata) => JSON.stringify(rowdata) === addRowData);
    if (exists.length !== 0) return enqueueSnackbar(t('CalendarCreate.message.alreadyRegistered'), { variant: 'warning' });

    // add to rowdata
    // リマインドデータは Redux に明細データを dispatch
    dispatch(setRowData([...rowData, user.entity]));
  };

  //-------------------------------------------------------------
  // 削除ボタンが押された時の処理
  //-------------------------------------------------------------
  const contextMenu: GetContextMenuItems = (params) => {
    if (params.node === null) return [];

    const menuItems = [
      {
        name: t('button.delete'),
        action: () => dispatch(setRowData(rowData.filter((row, i) => i !== params.node?.rowIndex))),
      },
    ];

    return menuItems;
  };

  //-------------------------------------------------------------
  // useEffect
  //-------------------------------------------------------------
  useEffect(() => {
    if (props.open) {
      updateUserSuggest();
    }
  }, [props.open]); // eslint-disable-line

  //-------------------------------------------------------------
  // SxProps
  //-------------------------------------------------------------
  const modalStyle: SxProps = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    width: '80vw',
    bgcolor: isLight ? palette.light.background.paper : palette.dark.background.paper,
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };
  const tableStyle: SxProps = {
    height: '300px',
    width: '100%',
    transition: 'height 0.5s',
  };

  // 登録処理
  const onClickRegister = async () => {
    if (rowData.length === 0) return enqueueSnackbar(t('CalendarCreate.message.userNotAdded'), { variant: 'error' });
    const valid = await methods.trigger();
    if (!valid) {
      // エラーある
      return;
    }
    const maxNo = reminderListData.length > 0 ? Math.max(...reminderListData.map((item) => Number(item.addRowNum ?? 0))) : 0;
    // 追加リマインドデータ編集
    // リマインドのメールアドレス ＝ 明細.メールアドレス
    // リマインド宛先名（日本語） ＝ 明細.氏名（日本語）
    // リマインド宛先名（英語） ＝ 明細.氏名（英語）
    // リマインド宛先のメールアドレス ＝ 明細.メールアドレス
    // リマインド宛先のBluePageメールアドレス ＝ 明細.BluePageメールアドレス
    // 送信日付 ＝ 画面.リマインド日付
    // リマインドステータス ＝ 送信待ち
    // 更新フラグ ＝ 更新
    const newReminders: ReminderEntity[] = rowData.map((item, index) => ({
      reminderUserId: item.email ?? '',
      reminderNameJpn: item.fullNameJpn ?? '',
      reminderNameEng: item.fullNameEng ?? '',
      reminderAddress: item.email ?? '',
      reminderBluePageEmailAddress: item.bluePageEmailAddress ?? '',
      sentAt: new Date(methods.getValues('sentAt') - new Date().getTimezoneOffset() * 60 * 1000).toISOString(),
      status: REMINDER_STATUS.ACTIVE,
      updateFlag: true,
      addRowNum: maxNo + index + 1,
    }));
    // リマインドデータに追加
    dispatch(setReminderListData([...reminderListData, ...newReminders]));
    // 画面データクリア
    clearData();
    // 画面を閉じる
    onClose();
  };

  // キャンセル処理関数
  const cancel = () => {
    // データ設定しない場合
    if (rowData.length === 0) {
      // 画面データクリア
      clearData();
      // モーダルを閉じる
      onClose();
    } else {
      // 確認ダイアログを開く
      setConfirmOpen(true);
    }
  };

  // キャンセル確認処理ハンドラー
  const handleCancelConfirm = () => {
    // 確認ダイアログを閉じる
    setConfirmOpen(false);
  };

  // いいえ確認処理ハンドラー
  const handleNoconfirm = () => {
    // 画面データクリア
    clearData();
    // 確認ダイアログを閉じる
    setConfirmOpen(false);
    // モーダルを閉じる
    onClose();
  };

  // 確認処理ハンドラー
  const handleConfirm = async () => {
    // 確認ダイアログが開いている場合
    if (confirmOpen) {
      // 確認ダイアログを閉じる
      setConfirmOpen(false);
    }
    // 登録処理呼出
    onClickRegister();
  };

  // 画面データクリア
  const clearData = () => {
    // 明細データクリア
    dispatch(setRowData([]));
    // 明ユーザークリア
    dispatch(setSelectedUser(null));
    // リマインド日付クリア
    methods.setValue('reminderDate', null);
    // リマインド日付エラークリア
    methods.clearErrors('reminderDate');
  };

  return (
    // リマインド依頼画面
    <Modal open={open} onClose={cancel}>
      <Box sx={modalStyle}>
        <Stack spacing={2}>
          {/* modal title */}
          <Grid container>
            <Grid item xs={12}>
              {/* リマインド依頼 */}
              <Typography variant={'h4'} component={'h2'} children={t('CalendarCreate.reminderRequest.pageTitle')} />
            </Grid>
          </Grid>

          {/* conditions */}
          <Grid container alignItems={'center'} justifyContent={'space-between'}>
            {/* user selector */}
            <Grid item xs={12} sm={10}>
              <Autocomplete
                // サイズ
                size="small"
                // 幅を100%にする
                fullWidth
                // 選択値
                value={selectedUser || null}
                // 入力欄を描画する関数
                renderInput={(params) => <TextField {...params} label={t('CalendarCreate.reminderRequest.selectConfirmer')} />}
                // 選択肢
                options={userSuggest.map((item) => item.key)}
                // 変更イベント
                onChange={(e, v) => dispatch(setSelectedUser(v))}
                // 入力値変更イベント
                onInputChange={(e, v, r) => (r !== 'reset' ? updateUserSuggest(v) : undefined)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Stack direction={'row-reverse'}>
                <Button
                  // 左側に表示するアイコン
                  startIcon={<AddIcon />}
                  fullWidth={useMediaQuery(theme.breakpoints.down('sm'))}
                  // 塗りつぶし
                  variant={'contained'}
                  // クリックイベント（追加）
                  onClick={onAddList}
                  // 子要素
                  children={t('button.add')}
                />
              </Stack>
            </Grid>
          </Grid>

          {/* data table */}
          <Grid container>
            <Box sx={tableStyle} className={isLight ? 'ag-theme-alpine' : 'ag-theme-alpine-dark'}>
              <SelectUserAgGrid rowData={rowData} onGridReady={onGridReady} contextMenu={contextMenu} />
            </Box>
          </Grid>

          {/* data table footer */}
          <Grid container direction="column" spacing={2}>
            {/* // Grid コンテナを縦方向カラムで設定 */}
            <Grid item container direction="row" justifyContent="flex-left" style={{ paddingLeft: 0 }} columnGap={'10px'}>
              {/* // Grid アイテムを幅で設定 */}
              <Grid item xs={12} md={'auto'}>
                {/* リマインド日付 */}
                <Typography variant="subtitle1" sx={{ wordBreak: 'keep-all', textAlign: 'left', mt: 1 }}>
                  {t('CalendarCreate.reminderRequest.reminderDate')}
                </Typography>
              </Grid>
              {/* // Grid アイテムを幅で設定 */}
              <Grid item xs={12} md={2}>
                <RHFDeskTopDatePicker
                  // フォーム制御オブジェクト
                  control={methods.control}
                  // 入力マスク
                  mask="____/__/__"
                  // フィールド名
                  name="reminderDate"
                  // 表示ラベル
                  label={t('CalendarCreate.reminderRequest.reminderDate')}
                  // バリデーションルール
                  rules={reminderDateRule}
                  // デフォルト値
                  defaultValue={null}
                  // 日付選択の表示単位:年月日
                  views={['year', 'month', 'day']}
                  // リマインド日付フォマード
                  inputFormat={'yyyy/MM/dd'}
                  fullWidth
                  // 変更イベント
                  onChange={(value) => {
                    methods.setValue('sentAt', value);
                  }}
                />
              </Grid>
            </Grid>
            <Grid item container justifyContent="flex-end" alignItems="center" xs={12} md={12} spacing={1}>
              {/* // Grid アイテムを全幅で設定 */}
              <Grid item xs={12} md={12} sx={{ width: '100%', textAlign: 'right' }}>
                {/* 登録ボタン */}
                <Button
                  // 塗りつぶし
                  variant={'contained'}
                  // クリックイベント（登録）
                  onClick={methods.handleSubmit(onClickRegister)}
                  // 登録
                  children={t('button.register')}
                  // スタイル設定
                  sx={{ ml: 2, width: 88, mr: 2 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Stack>

        {/* 確認ダイアログ（Dialog） */}
        <Dialog open={confirmOpen} onClose={handleCancelConfirm}>
          {/* 閉じるボタン（IconButton） */}
          <IconButton
            // aria-label 属性
            aria-label="close"
            // クリックイベント（キャンセル確認）
            onClick={handleCancelConfirm}
            // スタイル設定（位置、色）
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            {/* 閉じるアイコン */}
            <CloseIcon />
          </IconButton>
          {/* <DialogTitle>確認</DialogTitle> */}
          {/* コメントアウトされたダイアログタイトル */}
          {/* ダイアログコンテンツ（確認メッセージ） */}
          <DialogContent sx={{ pt: 6, pb: 0 }}>
            {/* 確認メッセージテキスト（改行対応） */}
            <Typography sx={{ whiteSpace: 'pre-line' }}>{t('CalendarCreate.message.saveConfirmMsg')}</Typography>
          </DialogContent>
          {/* ダイアログアクション（ボタン配置） */}
          <DialogActions>
            {/* いいえボタン（エラー色） */}
            <StyledButton variant="contained" onClick={handleNoconfirm} color="error">
              {/* 翻訳されたいいえラベル */}
              {t('button.no')}
            </StyledButton>
            {/* はいボタン（プライマリ色） */}
            <StyledButton variant="contained" onClick={handleConfirm} color="primary">
              {/* 翻訳されたはいラベル */}
              {t('button.yes')}
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};
